import { DateTime } from 'luxon';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as signalR from '@microsoft/signalr';
import { getConfig } from '../../config';
import useApiEndpoint from '../ApiEndpoint';
import _ from 'lodash';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '../..';
import { clearUserData } from 'utils/cleardata';

const signalREndpoint = getConfig().pushService.url;

const subTtl = 60 * 10;
const subTtlThreshold = 30;
const subRefreshInterval = subTtl - subTtlThreshold;
const subscriptionUpdateLoopTimer = 2000;

const usePushService = () => {
  const api = useApiEndpoint(`${signalREndpoint}`);
  const connection = useRef();
  const telemetryCallbacks = useRef([]);
  const subscriptionRefreshCounter = useRef();


  const [connectionState, setConnectionState] = useState(null);

  const subs = useRef({
    alarms: {
      config: {
        topicName: 'alarms',
        samplingType: 'first',
        samplingInterval: 8,
        assetIds: []
      },
      lastSyncedConfig: {},
      activeSubId: null
    },
    telemetry: {
      config: {
        topicName: 'telemetry',
        samplingType: 'first',
        samplingInterval: 1,
        assetIds: []
      },
      lastSyncedConfig: {},
      activeSubId: null
    }
  });

  const modifyAssetIdFilter = useCallback((topic, assetIdsToAdd, assetIdsToRemove) => {
    const sub = subs.current[topic];

    if (!sub) {
      console.error(`Tried to modify push service subscription filter for unsupported topic (${topic})`);
      return;
    }

    const subConfig = sub.config;

    _.each(assetIdsToAdd, assetId => {
      const existing = _.find(subConfig.assetIds, ['assetId', assetId]);

      if (existing) {
        ++existing.count;
      } else {
        subConfig.assetIds.push({
          assetId,
          count: 1
        });
      }
    });

    _.each(assetIdsToRemove, assetId => {
      const existing = _.find(subConfig.assetIds, ['assetId', assetId]);

      if (existing && --existing.count === 0) {
        _.remove(subConfig.assetIds, ['assetId', assetId]);
      }
    });

    _.each(subs.current, sub => {
      if (_.isEmpty(sub.config.assetIds)) {
        if (sub.activeSubId) {
          api.request('delete', `subscription/${sub.activeSubId}`);
          sub.activeSubId = null;
        }
      }
    });
  }, [api]);

  const getToken = async () => {
    const accounts = msalInstance.getAllAccounts();
    const account = msalInstance.getAccountByLocalId(accounts[0].localAccountId ?? null);
    const request = {
      scopes: [getConfig().msalConfig.auth.clientId],
      account: account
    };
    return msalInstance.acquireTokenSilent(request).then((response) => {
      return response.idToken;
    }).catch((e) => {
      clearUserData();
      if (e instanceof InteractionRequiredAuthError) {
        msalInstance.acquireTokenRedirect(request);
      }
    });
  };

  const subscribeToOrUpdateTopic = useCallback(async (subConfig) => {
    if (subConfig.id) {
      try {
        const result = await api.put(`subscription/${subConfig.id}`, subConfig);

        return result.data.id;
      } catch (error) {
        if (error.response?.status !== 404) {
          throw (error);
        }
      }
    }

    try {
      const result = await api.post('subscription', subConfig);

      return result.data.id;
    } catch (error) {
      // propagate error for push service
      console.error(error);
    }
  }, [api]);

  const postSubUpdate = useCallback((sub) => {
    const subConfigObj = {
      id: sub.activeSubId,
      connectionId: connection.current.connectionId,
      topic: sub.config.topicName,
      assets: _.map(sub.config.assetIds, 'assetId'),
      samplingType: sub.config.samplingType,
      sampleIntervalSeconds: sub.config.samplingInterval
    };

    subscribeToOrUpdateTopic(subConfigObj)
      .then(subId => {
        sub.activeSubId = subId;
      });
    sub.lastSyncedTimestamp = DateTime.utc();
  }, [subscribeToOrUpdateTopic]);

  const updateSubscriptionLoop = useCallback(() => {
    _.forIn(subs.current, sub => {
      const hasFilter = !_.isEmpty(sub.config.assetIds);
      const hasChanged = !_.isEqual(sub.config, sub.lastSyncedConfig);
      const needsRefresh = sub.lastSyncedTimestamp
        ? sub.lastSyncedTimestamp?.diffNow('seconds').values.seconds < -subRefreshInterval
        : true;

      if ((hasChanged || needsRefresh) && hasFilter) {
        postSubUpdate(sub);

        if (hasChanged) {
          sub.lastSyncedConfig = { ...sub.config, assetIds: [...sub.config.assetIds] };
        }
      }
    });
  }, [postSubUpdate]);

  useEffect(() => {
    subscriptionRefreshCounter.current = setInterval(updateSubscriptionLoop, subscriptionUpdateLoopTimer);

    return () => {
      clearInterval(subscriptionRefreshCounter.current);
    };
  }, [updateSubscriptionLoop]);

  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [hasError, setError] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const isConnecting = useRef(false);

  const connect = useCallback(async () => {
    if (isConnecting.current) {
      return;
    }

    isConnecting.current = true;
    setLoading(true);
    setError(false);
    // await updateTokenCache();
    setConnectionState(null);
    connection.current = new signalR.HubConnectionBuilder()
      .withUrl(signalREndpoint, { accessTokenFactory: getToken })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // To update the connection ID in the subscription
    connection.current.onreconnected(() => _.forIn(subs.current, postSubUpdate));

    try {
      await connection.current.start();
      if (mounted.current) {
        setConnectionState(connection.current);
      }
      setLoading(false);
      isConnecting.current = false;
    } catch (e) {
      setLoading(false);
      isConnecting.current = false;
      setError(true);
      console.error('Failed to connect to push service', e);
    }
  }, [postSubUpdate]);

  const getDebugInfo = useCallback(() => {
    return {
      isConnected: !!connection.current,
      numTelemetryListeners: telemetryCallbacks.current.length
    };
  }, []);

  return useMemo(() => ({
    getDebugInfo,
    connectionState,
    connect,
    hasError,
    isLoading,
    modifyAssetIdFilter
  }), [getDebugInfo, connectionState, connect, hasError, isLoading, modifyAssetIdFilter]);
};

const PushService = createContext();

/**
 * Subscribe to a specific event `name` and get callbacks on `callback` when it receives events
 * for the assets specified, by ID, in 'assetIdFilter'.
 * This will also handle unsubscribe when component dies
 * @param topic
 * @param callback
 * @param assetIdFilter
 * @example
 * usePushServiceSubscription('alarms', onAlarmsUpdatedCallback, ['asset-1', 'asset-2']);
 */
const usePushServiceSubscription = (topic, callback, assetIdFilter) => {

  // TODO: Get errors from the push service

  const prevArguments = useRef(null);
  const prevAssetIdFilter = useRef([]);
  const { connectionState, connect, hasError, isLoading, modifyAssetIdFilter } = useContext(PushService);

  const init = useCallback(async () => {
    if (!connectionState) {
      await connect();
    }
  }, [connectionState, connect]);

  useEffect(() => {
    init();
  }, [init]);

  const _callback = useCallback(response => {
    callback(JSON.parse(response));
  }, [callback]);

  useEffect(() => {
    if (prevArguments.current && connectionState) {
      connectionState.off(...prevArguments.current);
      prevArguments.current = null;
    }

    if (connectionState) {
      connectionState.on(topic, _callback);
      prevArguments.current = [topic, _callback];
    }

    return () => {
      if (prevArguments.current && connectionState) {
        connectionState.off(...prevArguments.current);
        prevArguments.current = null;
      }
    };
  }, [topic, _callback, connectionState]);

  useEffect(() => {
    if (!assetIdFilter) {
      return;
    }

    modifyAssetIdFilter(topic, assetIdFilter, prevAssetIdFilter.current);
    prevAssetIdFilter.current = assetIdFilter;
  }, [assetIdFilter, modifyAssetIdFilter, topic]);

  useEffect(() => {
    return () => {
      modifyAssetIdFilter(topic, [], prevAssetIdFilter.current);
    };
  }, [modifyAssetIdFilter, topic]);

  return [isLoading, hasError];
};

export { usePushService, usePushServiceSubscription, PushService };
